<template>
	<Listbox
		v-slot="{ open }"
		v-model="selected"
		as="div"
		:multiple="multiple"
	>
		<ListboxLabel
			v-if="label"
			:class="[
				staticLabel ? 'sr-only' : labelClass,
				required ? 'after:content-[\'*\'] after:font-bold after:text-red-500 after:ml-1' : ''
			]"
		>
			{{ label }}
		</ListboxLabel>
		<div class="relative border-none">
			<ListboxButton as="template">
				<button
					ref="buttonRef"
					:class="[buttonClasses, buttonHeight]"
				>
					<span
						class="block truncate"
						:class="[
							{ 'text-mx-gray-500': isPlaceholderShown }
						]"
					>
						{{ displayText }}
					</span>
					<span class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
						<ChevronUpDownIcon
							class="w-5 h-5 text-mx-gray-400"
							aria-hidden="true"
						/>
					</span>
				</button>
			</ListboxButton>
		</div>

		<!-- Teleport the dropdown to the body -->
		<Teleport to="body">
			<Transition
				enter-active-class="transition duration-100 ease-out"
				enter-from-class="translate-y-1 opacity-0"
				enter-to-class="translate-y-0 opacity-100"
				leave-active-class="transition duration-100 ease-in"
				leave-from-class="translate-y-0 opacity-100"
				leave-to-class="translate-y-1 opacity-0"
				@before-enter="handleOpen"
			>
				<ListboxOptions
					v-if="open"
					ref="dropdownRef"
					:style="dropdownStyle"
					:class="[
						'z-[9999] py-1 overflow-auto text-base min-w-72 rounded-md shadow-lg dark:shadow-white/5 ring-1 ring-mx-orange ring-opacity-5 focus:outline-none sm:text-sm',
						optionWidth,
						optionHeight,
						optionBgColor
					]"
				>
					<ListboxOption
						v-for="option in options"
						:key="option.id + option.name"
						v-slot="{ active, selected: newSelected }"
						as="template"
						:value="option"
						:disabled="option.disabled"
						class="min-w-72"
					>
						<li
							:class="[
								'relative select-none py-2 pl-3 pr-9 capitalize',
								active ? 'bg-mx-gray-100 dark:bg-mx-green-600 text-black dark:text-white' : 'text-mx-gray-500 dark:text-mx-gray-300',
								option.disabled ? 'text-mx-gray-300/90 cursor-not-allowed dark:text-mx-gray-500' : 'cursor-pointer'
							]"
						>
							<span
								:class="[
									newSelected ? 'font-semibold' : 'font-normal',
									'block truncate mr-2'
								]"
							>
								{{ option.name }}
							</span>
							<span
								v-if="newSelected"
								:class="[
									active ? 'text-white' : 'text-mx-orange',
									'absolute inset-y-0 right-0 flex items-center pr-4'
								]"
							>
								<CheckIcon
									class="w-5 h-5"
									aria-hidden="true"
								/>
							</span>
						</li>
					</ListboxOption>
				</ListboxOptions>
			</Transition>
		</Teleport>

		<div
			v-if="showInputInstruction"
			class="py-1"
		>
			<slot name="instruction">
				<div
					class="pl-1 text-xs italic text-mx-gray-500 dark:text-mx-gray-400"
					v-html="inputInstruction"
				/>
			</slot>
		</div>
	</Listbox>
</template>

<script setup lang="ts">
import type { SelectOption } from '@/components/form/types'
import {
	Listbox,
	ListboxButton,
	ListboxLabel,
	ListboxOption,
	ListboxOptions
} from '@headlessui/vue'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/vue/20/solid'

const props = defineProps({
	label: {
		type: String,
		required: true
	},
	options: {
		type: Array as PropType<SelectOption[]>,
		required: true
	},
	labelClass: {
		type: String,
		default: 'text-sm flex font-semibold uppercase text-mx-gray-500 dark:text-mx-gray-300 whitespace-nowrap min-h-5'
	},
	showInputInstruction: {
		type: Boolean,
		default: false
	},
	inputInstruction: {
		type: String,
		default: ''
	},
	staticLabel: {
		type: Boolean,
		default: false
	},
	optionWidth: {
		type: String,
		default: 'w-full'
	},
	optionHeight: {
		type: String,
		default: ''
	},
	optionBgColor: {
		type: String,
		default: 'bg-white dark:bg-mx-green-800'
	},
	required: {
		type: Boolean,
		default: false
	},
	placeholder: {
		type: String,
		default: ''
	},
	buttonClasses: {
		type: String,
		default: 'relative w-full pl-3 pr-10 text-left text-black bg-white border rounded-md shadow-sm cursor-pointer dark:shadow-inner dark:border-none dark:bg-mx-green-700 dark:text-mx-gray-300 focus:outline-none focus:ring-2 focus:ring-mx-orange sm:text-sm sm:leading-6 capitalize'
	},
	buttonHeight: {
		type: String,
		default: 'h-11'
	},
	multiple: {
		type: Boolean,
		default: false
	}
})

const selected = defineModel<SelectOption | SelectOption[]>()

const {
	label, options, placeholder, multiple
} = toRefs(props)

// Refs for button and dropdown elements
const buttonRef = ref<HTMLElement | null>(null)
const dropdownRef = ref<HTMLElement | null>(null)

const DROPDOWN_Y_OFFSET_PX = 6 // Offset from the button's bottom edge

// Computed properties for display
const isPlaceholderShown = computed(() => {
	if (multiple.value) {
		return Array.isArray(selected.value) && selected.value.length === 0
	}
	return !multiple.value && (selected.value as SelectOption)?.id === null
})

const displayText = computed(() => {
	if (props.staticLabel) {
		return label.value
	}

	if (multiple.value) {
		const selectedArray = selected.value as SelectOption[]
		if (!selectedArray || selectedArray.length === 0) {
			return placeholder.value || 'Select options'
		}
		if (selectedArray.length === 1) {
			return selectedArray[0].name
		}
		return `${selectedArray.length} selected`
	} else {
		const singleSelected = selected.value as SelectOption
		return singleSelected?.name || placeholder.value || 'Select option'
	}
})

// Reactive style object for the dropdown
const dropdownStyle = ref<Record<string, string>>({
	position: 'absolute',
	top: `${0 + DROPDOWN_Y_OFFSET_PX}px`,
	left: '0px',
	width: 'auto'
})

// Update dropdown position based on the button's bounding rect
// and adjust the x-axis position if the dropdown would overflow the window width
const updateDropdownPosition = () => {
	if (!buttonRef.value) {
		return
	}
	const rect = buttonRef.value.getBoundingClientRect()
	const top = rect.bottom + window.scrollY + DROPDOWN_Y_OFFSET_PX
	let left = rect.left + window.scrollX
	const dropdownWidth = rect.width
	const viewportRight = window.scrollX + window.innerWidth

	// If dropdown overflows to the right, move it to the left edge
	if (left + dropdownWidth > viewportRight) {
		left = viewportRight - dropdownWidth
	}

	dropdownStyle.value = {
		position: 'absolute',
		top: `${top}px`,
		left: `${left}px`,
		width: `${dropdownWidth}px`
	}
}

// Handler for scroll and resize events
const onScroll = () => {
	updateDropdownPosition()
}

onMounted(() => {
	window.addEventListener('scroll', onScroll, true)
	window.addEventListener('resize', updateDropdownPosition)

	// Initialize default value if not set
	if (!selected.value) {
		if (multiple.value) {
			selected.value = []
		} else {
			selected.value = {
				id: null,
				name: placeholder.value || 'Select option'
			}
		}
	} else if (placeholder.value && !multiple.value) {
		const singleSelected = selected.value as SelectOption
		if (singleSelected.id === null) {
			selected.value = {
				id: null,
				name: placeholder.value
			}
		}
	}
})

onUnmounted(() => {
	window.removeEventListener('scroll', onScroll, true)
	window.removeEventListener('resize', updateDropdownPosition)
})

// Use Transition hook to update position when the dropdown is about to appear
const handleOpen = () => {
	nextTick(() => {
		updateDropdownPosition()
	})
}
</script>

<style scoped>
::-webkit-scrollbar {
	width: 8px;
	height: 8px;
}

::-webkit-scrollbar-track {
	background: transparent;
	border-radius: 10px;
}

::-webkit-scrollbar-thumb {
	background: #6A6A6A;
	border-radius: 10px;
}

::-webkit-scrollbar-thumb:hover {
	background: #ADADAD;
}

::-webkit-scrollbar-button {
	display: none;
}

* {
	scrollbar-width: thin;
	scrollbar-color: #6A6A6A transparent;
}
</style>
